import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { Alert, Box, Button, Card, InputAdornment, LinearProgress, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthContext';
import { PERMISSIONS } from '@/auth/permissions';
import { PageHeader } from '@/shared/PageHeader';
import { useSnackbar } from '@/shared/snackbar/SnackbarContext';
import { useDeleteVehicle, useSetAvailability, useVehicles } from './api';
import { ConfirmDialog } from './components/ConfirmDialog';
import { VehicleDrawer } from './components/VehicleDrawer';
import { VehicleFormDialog } from './components/VehicleFormDialog';
import { VehicleTable } from './components/VehicleTable';
import type { Vehicle, VehicleDetail } from './types';

type DialogState = { mode: 'create' } | { mode: 'edit'; vehicle: VehicleDetail } | null;

function matches(vehicle: Vehicle, query: string): boolean {
    return [vehicle.displayName, vehicle.licensePlate, vehicle.notes ?? ''].some((text) =>
        text.toLowerCase().includes(query),
    );
}

export function VehiclesPage() {
    const { t } = useTranslation();
    const { can } = useAuth();
    const { showError, showSuccess } = useSnackbar();
    const canEdit = can(PERMISSIONS.VEHICLE_EDIT);

    const vehicles = useVehicles();
    const setAvailability = useSetAvailability();
    const deleteVehicle = useDeleteVehicle();

    const [search, setSearch] = useState('');
    const [dialog, setDialog] = useState<DialogState>(null);
    const [drawerId, setDrawerId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<VehicleDetail | null>(null);

    const all = vehicles.data ?? [];
    const query = search.trim().toLowerCase();
    const rows = query ? all.filter((vehicle) => matches(vehicle, query)) : all;

    function onToggleAvailability(vehicle: Vehicle, available: boolean) {
        if (setAvailability.isPending) {
            return;
        }
        setAvailability.mutate({ id: vehicle.id, available }, { onError: showError });
    }

    function onConfirmDelete() {
        if (!deleteTarget) {
            return;
        }
        deleteVehicle.mutate(deleteTarget.id, {
            onSuccess: () => {
                setDeleteTarget(null);
                setDrawerId(null);
                showSuccess(t('vehicles.delete.done'));
            },
            onError: showError,
        });
    }

    const addButton = (
        <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => setDialog({ mode: 'create' })}
        >
            {t('vehicles.add')}
        </Button>
    );

    return (
        <>
            <PageHeader title={t('vehicles.title')} subtitle={t('vehicles.subtitle')} actions={canEdit && addButton} />
            <Box sx={{ p: 3 }}>
                <Card>
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <TextField
                            type="search"
                            size="small"
                            fullWidth
                            sx={{ maxWidth: { sm: 384 } }}
                            placeholder={t('vehicles.searchPlaceholder')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Box>
                    {vehicles.isPending && <LinearProgress />}
                    {vehicles.isError && (
                        <Alert severity="error" sx={{ m: 2 }}>
                            {t('vehicles.loadFailed')}
                        </Alert>
                    )}
                    {vehicles.isSuccess && all.length === 0 && (
                        <Box sx={{ px: 3, py: 8, textAlign: 'center' }}>
                            <Typography variant="h6" gutterBottom>
                                {t('vehicles.empty.title')}
                            </Typography>
                            <Typography color="text.secondary" sx={{ mb: 2, mx: 'auto', maxWidth: '52ch' }}>
                                {canEdit ? t('vehicles.empty.text') : t('vehicles.empty.textEmployee')}
                            </Typography>
                            {canEdit && addButton}
                        </Box>
                    )}
                    {/* Cached rows survive a failed refetch — never offer the switches on stale data. */}
                    {all.length > 0 && !vehicles.isError && (
                        <>
                            <VehicleTable
                                rows={rows}
                                canEdit={canEdit}
                                pending={setAvailability.isPending}
                                onOpen={(vehicle) => setDrawerId(vehicle.id)}
                                onToggleAvailability={onToggleAvailability}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                                {t('vehicles.countOf', { shown: rows.length, total: all.length })}
                            </Typography>
                        </>
                    )}
                </Card>
            </Box>
            {canEdit && (
                <>
                    <VehicleDrawer
                        id={drawerId}
                        onClose={() => setDrawerId(null)}
                        onEdit={(vehicle) => setDialog({ mode: 'edit', vehicle })}
                        onDelete={setDeleteTarget}
                    />
                    {dialog && (
                        // Mounted only while open so every session starts with fresh fields.
                        <VehicleFormDialog
                            open
                            vehicle={dialog.mode === 'edit' ? dialog.vehicle : null}
                            onClose={() => setDialog(null)}
                        />
                    )}
                    <ConfirmDialog
                        open={deleteTarget !== null}
                        title={t('vehicles.delete.title')}
                        message={t('vehicles.delete.text', { name: deleteTarget?.displayName ?? '' })}
                        confirmLabel={t('common.delete')}
                        color="error"
                        busy={deleteVehicle.isPending}
                        onCancel={() => setDeleteTarget(null)}
                        onConfirm={onConfirmDelete}
                    />
                </>
            )}
        </>
    );
}
